import { Image } from "../shared/image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { motion } from "framer-motion";
import { useNomineesCtx } from "./contexts/nomination";
import { Skeleton } from "../ui/skeleton";
import frame from "public/Frame.svg";
import tag from "public/2026_winner.svg";

type CardDetailsProps = {
  isWinner?: boolean;
  isActive?: boolean;
  name: string;
  title: string;
  description: string;
  year: number;
};

const getFontSize = (length: number) => {
  if (length <= 10) {
    return "text-3xl xs:text-4xl sm:text-4xl md:text-4xl";
  } else if (length <= 20) {
    return "text-2xl xs:text-3xl sm:text-2xl md:text-4xl";
  } else {
    return "text-xl sm:text-2xl md:text-3xl";
  }
};

export function CardDetailSkeleton() {
  return (
    <motion.div
      className="mt-14 flex h-[300px] flex-col items-center gap-4 text-center lg:mt-0 lg:justify-center"
      initial={{ opacity: 0.5 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <Skeleton className="h-8 w-40 rounded-md md:w-60" />
      <Skeleton className="h-6 w-40 rounded-md md:w-60" />
      <Skeleton className="h-24 w-60 rounded-md sm:w-80 xl:w-96" />
    </motion.div>
  );
}

export function CardDetail({
  isWinner,
  isActive,
  name,
  title,
  year,
  description,
}: CardDetailsProps) {
  const fontSizeClass = getFontSize(name.length);

  if (!isActive) return;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0, duration: 1, ease: "easeOut" }}
      className="mt-14 flex h-[300px] flex-col items-center gap-4 text-center lg:mt-0 lg:justify-center"
    >
      {isWinner && (
        <div className="rounded-[10px] border border-primary px-3 py-2 text-[#F3D5A9]">
          <span>WINNER</span>
        </div>
      )}
      <h1
        className={`max-w-[200px] md:max-w-[300px] ltr:font-playfair ${fontSizeClass} text-secondary`}
      >
        {name}
      </h1>
      <h2 className="max-w-[200px] text-sm text-[#F3D5A9] sm:text-base md:max-w-[300px] xl:text-lg ltr:font-opensans">
        {title} - {year}
      </h2>
      <p className="text-balance px-2 text-sm text-secondary opacity-60 sm:max-w-[400px] sm:px-0 lg:max-w-[500px] xl:text-base ltr:font-opensans">
        {description}
      </p>
    </motion.div>
  );
}

type CardProps = {
  isWinner: boolean;
  src: string;
};

export default function Card({ isWinner, src }: CardProps) {
  const { loading } = useNomineesCtx();
  return (
    <div className="">
      <AspectRatio>
        <div
          className={
            "relative h-full w-full overflow-hidden rounded-2xl xl:rounded-[30px] 2xl:rounded-[40px]"
          }
        >
          {loading ? (
            <Skeleton className="absolute inset-0 h-full w-full rounded-2xl xl:rounded-[30px] 2xl:rounded-[40px]" />
          ) : (
            <>
              {isWinner && (
                <div className="relative z-20 flex h-full w-full items-center justify-center">
                  <Image
                    draggable={false}
                    src={frame}
                    alt="Frame"
                    fill
                    objectFit="contain"
                    className="z-20 object-contain"
                  />
                  <Image
                    draggable={false}
                    src={tag}
                    alt="Tag"
                    className="absolute bottom-2 left-2 z-20 object-contain"
                    width={150}
                  />
                  <Image
                    sizes="400px"
                    src={src}
                    alt="image"
                    fill
                    className="scale-95 object-contain"
                  />
                </div>
              )}
              {!isWinner && (
                <Image sizes="400px" src={src} alt="image" fill className="object-contain" />
              )}
            </>
          )}
        </div>
      </AspectRatio>
    </div>
  );
}
